/**
 * TransferRow — one row in the `/cash/transfers` ledger table.
 *
 * Redesigned around a single "flow" cell: an account avatar + name for the
 * source, a connector arrow, then an account avatar + name for the
 * destination — so a transfer reads as money moving from one account to
 * another at a glance. Avatars reuse the bank-accounts listing palette
 * (per-bank colour, `₹` for cash, `VISA` for cards), so account identity is
 * consistent across the cash module. Account-type is carried by the avatar +
 * the subtype pill, so the old per-account type chips are dropped.
 *
 * Rendered as a `<tr>` so callers compose it inside the `<table>`. Columns:
 *   ┌──────┬───────────────────────────────┬────────┬───────────┬─────────┐
 *   │ date │ from ●→ to (avatars + arrow)   │ amount │ reference │ subtype │
 *   └──────┴───────────────────────────────┴────────┴───────────┴─────────┘
 *
 * @see prototype/cash/transfers.html `.tx-table`
 */
import { useNavigate } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { paiseToInr } from "../../../lib/format.ts";
import { SubtypePill } from "./subtype-pill.tsx";
import type { BankAccountType, TransferSubtype } from "../lib/classify-subtype.ts";

export interface TransferRowProps {
  id: string;
  /** Display string like `"14 May"`, already formatted by the caller. */
  valueDate: string;
  fromAccount: { name: string; type: BankAccountType };
  toAccount: { name: string; type: BankAccountType };
  /** Amount in paise (positive integer). */
  amountPaise: string;
  /** `cash_movements.narration` — surfaces as the "Reference" column. */
  narration: string;
  subtype: TransferSubtype;
}

export function TransferRow({
  id,
  valueDate,
  fromAccount,
  toAccount,
  amountPaise,
  narration,
  subtype,
}: TransferRowProps) {
  const navigate = useNavigate();
  return (
    <tr
      className="border-b border-line last:border-0 hover:bg-raised transition-colors cursor-pointer"
      onClick={() => navigate({ to: "/cash/transfers/$movementId", params: { movementId: id } })}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          void navigate({ to: "/cash/transfers/$movementId", params: { movementId: id } });
        }
      }}
      tabIndex={0}
      role="link"
    >
      <td className="px-3 py-3 align-middle">
        <span className="num text-sm font-medium text-ink-2 whitespace-nowrap">{valueDate}</span>
      </td>
      <td className="px-3 py-3 align-middle">
        <div className="flex items-center gap-2.5 min-w-0">
          <AccountAvatar name={fromAccount.name} type={fromAccount.type} />
          <AccountName name={fromAccount.name} />
          <ArrowRight
            size={16}
            strokeWidth={2}
            className="mx-1 shrink-0 text-muted"
            aria-label="to"
          />
          <AccountAvatar name={toAccount.name} type={toAccount.type} />
          <AccountName name={toAccount.name} />
        </div>
      </td>
      <td className="px-3 py-3 text-right align-middle">
        <span className="num tabular-nums text-sm font-semibold text-ink whitespace-nowrap">
          {paiseToInr(amountPaise)}
        </span>
      </td>
      <td className="px-3 py-3 align-middle">
        <span className="block max-w-[240px] truncate text-xs text-ink-2" title={narration}>
          {narration}
        </span>
      </td>
      <td className="px-3 py-3 align-middle">
        <SubtypePill subtype={subtype} />
      </td>
    </tr>
  );
}

// ── account avatar ──────────────────────────────────────────────────
// Palette + labelling mirror the bank-accounts listing's `avatarFor`
// (apps/web/.../bank-accounts/index.tsx) so an account looks the same
// wherever it appears. Inline hex (not Tailwind colour classes) keeps it
// independent of the Tailwind colour config.

function accountAvatarMeta(
  name: string,
  type: BankAccountType,
): { bg: string; color: string; label: string; tiny: boolean } {
  if (type === "cash") return { bg: "#1e293b", color: "#fff", label: "₹", tiny: false };
  if (type === "credit_card") return { bg: "#0f172a", color: "#fde68a", label: "VISA", tiny: true };
  const n = name.toLowerCase();
  if (n.includes("hdfc")) return { bg: "#0284c7", color: "#fff", label: "H", tiny: false };
  if (n.includes("icici")) return { bg: "#ca8a04", color: "#1a1206", label: "I", tiny: false };
  if (n.includes("kotak")) return { bg: "#db2777", color: "#fff", label: "K", tiny: false };
  if (n.includes("axis")) return { bg: "#334155", color: "#fff", label: "A", tiny: false };
  if (n.includes("rbl")) return { bg: "#059669", color: "#fff", label: "R", tiny: false };
  if (n.startsWith("sbi") || n.includes("state bank"))
    return { bg: "#1d4ed8", color: "#fff", label: "S", tiny: false };
  return { bg: "#0284c7", color: "#fff", label: name.charAt(0).toUpperCase(), tiny: false };
}

function AccountAvatar({ name, type }: { name: string; type: BankAccountType }) {
  const a = accountAvatarMeta(name, type);
  return (
    <div
      className="grid h-8 w-8 shrink-0 place-items-center rounded-md font-bold"
      style={{
        background: a.bg,
        color: a.color,
        fontSize: a.tiny ? 9 : 12,
        letterSpacing: a.tiny ? "0.04em" : "-0.02em",
      }}
      aria-hidden
    >
      {a.label}
    </div>
  );
}

// Seeded names follow `<Bank> · <Label ···last4>`. Split on the first ` · `
// so the last-4 token renders muted beneath the bank name.
function AccountName({ name }: { name: string }) {
  const idx = name.indexOf(" · ");
  const bank = idx === -1 ? name : name.slice(0, idx);
  const acc = idx === -1 ? null : name.slice(idx + 3);
  return (
    <div className="min-w-0 leading-tight">
      <div className="max-w-[150px] truncate text-sm font-semibold text-ink">{bank}</div>
      {acc ? <div className="max-w-[150px] truncate text-xs text-muted">{acc}</div> : null}
    </div>
  );
}
